import { useQuery } from '@apollo/client'
import { BigNumber } from 'ethers'
import { useEffect, useState } from 'react'
import {
  LENDING_HISTORY_ITEM_QUERY,
  LendingUserHistoryItems
} from '../../../queries/lendingHistoryItemQuery'
import { toUnscaled } from '../../../utils/bn'
import { ASSET_DECIMALS } from '../../../constants'

export type LendingHistoryItem = {
  id: string
  address: string
  assetId: number
  account: string
  action: string
  assetAmount: number
  timestamp: number
}

export const NUM_ONE_PAGE_ITEMS = 500

export function useLendingHistory(
  account: string,
  contractAddress: string,
  assetId: number
) {
  const [skip, setSkip] = useState(0)
  const [lendingHistory, setLendingHistory] = useState<LendingHistoryItem[]>([])

  const { data } = useQuery<LendingUserHistoryItems>(
    LENDING_HISTORY_ITEM_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        owner: account.toLowerCase(),
        address: contractAddress.toLocaleLowerCase(),
        assetId: assetId,
        skip: skip,
        first: NUM_ONE_PAGE_ITEMS
      },
      pollInterval: 30000
    }
  )

  useEffect(() => {
    if (data) {
      const newItems = data.lendingUserHistoryItems.map(entity => {
        const assetAmount = BigNumber.from(entity.assetAmount)

        return {
          id: entity.id,
          address: entity.address.toLowerCase(),
          assetId: Number(entity.assetId),
          account: entity.account,
          action: entity.action === 'DEPOSIT' ? 'Supply' : 'Withdraw',
          assetAmount: toUnscaled(assetAmount, ASSET_DECIMALS[assetId]),
          timestamp: Number(entity.createdAt)
        }
      })

      setLendingHistory(history => {
        return history
          .concat(newItems)
          .map(item => Object.assign({}, item))
          .sort((a, b) => b.timestamp - a.timestamp)
          .filter(
            val =>
              val.address === contractAddress.toLowerCase() &&
              val.assetId === assetId
          )
          .filter(
            (val, ind, self) =>
              ind === self.findIndex(item => item.id === val.id)
          )
      })
    }
  }, [account, data, contractAddress, assetId])

  return {
    lendingHistory,
    skip,
    setSkip
  }
}
